import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "overlayscrollbars/styles/overlayscrollbars.css";
import App from "./App.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import SettingsRedirect from "./components/SettingsRedirect.jsx";
import OAuthCallback from "./pages/OAuthCallback.jsx";
import McpCallback from "./pages/McpCallback.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import RunConsentProvider from "./components/RunConsentProvider.jsx";
import "./styles.css";
import { bootstrapAuth, shouldRenderSelfHostedLogin } from "./lib/authBootstrap.js";
import { hydrateSignalCatalog } from "./lib/signalCatalog.js";
import { installAuthFetchShim } from "./lib/authFetchShim.js";
import { startAuthBroadcast } from "./lib/authBroadcast.js";
import { installLlmProxyShim } from "./lib/llmProxyShim.js";
import { DIRECTORY_OAUTH_CALLBACK_PATH } from "./lib/oauthCallback.js";

import { installChunkReloadHandler } from "./lib/chunkReload.js";
import { initAnalytics } from "./lib/analytics/client.js";
import { tenantAnalyticsRegion } from "./lib/analytics/tenantGate.js";
import { AnalyticsProvider, PostHogIdentity, reactRootErrorOptions } from "./lib/analytics/react.jsx";

installChunkReloadHandler();

// Install the auth fetch shim before any other code can issue a /v1
// request — including bootstrapAuth's `/v1/auth/exchange` and any
// import-time analytics warm-ups. The shim attaches `Authorization`
// from the in-process token store and captures /v1/auth/* responses
// into the same store, replacing the previous localStorage path.
// Must run before `installLlmProxyShim` so the call chain is
// `llmWrapper → authWrapper → native`: LLM-host requests still get
// rewritten to `/v1/tenant/llm-proxy`, and the auth wrapper then
// attaches the bearer token on the way out.
installAuthFetchShim();

// Open the cross-tab session bridge. Must come after the fetch shim
// installs (so the shim's first captured-tokens write is the one
// that gets broadcast — not a no-op against a not-yet-open channel)
// and before bootstrapAuth runs (so a freshly opened tab can adopt a
// sibling's session and skip the WorkOS redirect).
startAuthBroadcast();

// Install the in-process LLM-proxy fetch wrapper. Replaces the old
// `llm-proxy-sw.js` service worker we tore out — see
// `lib/llmProxyShim.js` for the why. The wrapper is in place
// synchronously, so chat code can assume future LLM fetches are
// intercepted from this point on.
installLlmProxyShim();

// Directory-integration OAuth landing page. Handled here, ahead of the
// regular auth bootstrap and the router, because the redirect URI is a
// fixed path and we don't want to drag the user through a WorkOS
// exchange just to finalize a Graph admin consent. (Under BrowserRouter
// this pre-router check is essential: otherwise the `path="*"` route
// below would match the callback path and render the app instead.) The
// path comes from the same constant the sender uses for `redirect_uri`,
// so the value we listen for can't drift from the value we register.
if (window.location.pathname === DIRECTORY_OAUTH_CALLBACK_PATH) {
  const root = document.getElementById("app");
  createRoot(root).render(
    <StrictMode>
      <OAuthCallback />
    </StrictMode>,
  );
} else if (window.location.pathname === "/mcp-callback") {
  // MCP OAuth landing page. Same flat-path-routed pattern as above —
  // each remote MCP server registers this URL as its redirect_uri
  // during dynamic client registration.
  const root = document.getElementById("app");
  createRoot(root).render(
    <StrictMode>
      <McpCallback />
    </StrictMode>,
  );
} else {
  // In auth-required modes (devint / staging / prod) this either
  // exchanges `?code=…` from a WorkOS callback for tokens (resolves
  // true) or kicks off a redirect to WorkOS (resolves false; page
  // navigates away before React mounts). In `development` mode it
  // resolves true immediately and we mount as before.
  //
  // When the self-hosted-login flag is on and there's no token,
  // bootstrap resolves false WITHOUT redirecting — we mount <LoginPage />
  // in place of <App />.
  bootstrapAuth().then(async (ready) => {
    const root = document.getElementById("app");
    let loginRoot = null;
    async function mountAuthenticatedApp() {
      // Product analytics starts only here, once auth has stripped the OAuth
      // `code` and `state` from the URL: the SDK keeps the first URL it sees
      // in the browser's storage, so the callback pages above never start it.
      // Resolves to the PostHog client in a deployed build whose environment
      // enables analytics, and to a no-op client otherwise.
      // It also starts only for a tenant with a product analytics region, and
      // only into that region's project, which it reads from the tenant
      // configuration before the SDK sends anything.
      const analyticsClient = await initAnalytics({ tenantRegion: () => tenantAnalyticsRegion() });
      // Warm the signals catalog once auth is confirmed, so the sidebar signals
      // facet + query layer (which read it synchronously, outside the hook) find
      // it hydrated. Fire-and-forget: it never throws (a 403 settles `denied`),
      // and signals surfaces still re-render via useSignalCatalog when it lands.
      hydrateSignalCatalog();
      loginRoot?.unmount();
      loginRoot = null;
      // The root error options report every caught, uncaught and recoverable
      // React error with its component stack; ErrorBoundary stays UI-only.
      createRoot(root, reactRootErrorOptions(analyticsClient)).render(
        <StrictMode>
          <ErrorBoundary>
            <AnalyticsProvider client={analyticsClient}>
              <PostHogIdentity />
              <BrowserRouter>
                <RunConsentProvider>
                  <Routes>
                    {/* Clean settings shortcuts redirect to the canonical
                        query-param URL (see SettingsRedirect). Everything
                        else is the app, which is query-param driven on `/`. */}
                    <Route path="/settings" element={<SettingsRedirect />} />
                    <Route path="/settings/:view" element={<SettingsRedirect />} />
                    <Route path="/endpoints" element={<SettingsRedirect mode="endpoints" />} />
                    <Route path="/endpoints/:view" element={<SettingsRedirect mode="endpoints" />} />
                    <Route path="/admin" element={<SettingsRedirect mode="admin" />} />
                    <Route path="/admin/:view" element={<SettingsRedirect mode="admin" />} />
                    <Route path="/governance" element={<SettingsRedirect mode="governance" />} />
                    <Route path="/governance/:view" element={<SettingsRedirect mode="governance" />} />
                    <Route path="/account" element={<SettingsRedirect mode="admin" fixedView="account" />} />
                    <Route path="/feedback" element={<SettingsRedirect mode="admin" fixedView="report" />} />
                    <Route path="*" element={<App />} />
                  </Routes>
                </RunConsentProvider>
              </BrowserRouter>
            </AnalyticsProvider>
          </ErrorBoundary>
        </StrictMode>,
      );
    }
    if (ready) return mountAuthenticatedApp();
    if (shouldRenderSelfHostedLogin()) {
      loginRoot = createRoot(root);
      loginRoot.render(
        <StrictMode>
          <LoginPage onAuthenticated={mountAuthenticatedApp} />
        </StrictMode>,
      );
    }
  });
}
